import { DragOverEvent } from '@dnd-kit/dom'
import { useMutation } from '@tanstack/react-query'
import uniqBy from 'lodash/uniqBy'
import { useState } from 'react'

import { getToday } from '~/astra'
import { useFlag } from '~/hooks/useFlag'
import { updateBatchDepartmentPractitionerAssignments, updateBatchPractitioners } from '~/store/di-entity.api'
import { dispatchSuccessToast } from '~/store/slices/toasterSlice'
import {
    type Department,
    type HospitalConfigPractitioner,
    isPractitionerActive,
    useAllPractitionersForHospitalConfig,
    useDepartments,
    useSpecialities,
} from '~/storm'
import { trackUserEvent } from '~/utils/analytics'
import { getPractitionerFullname } from '~/utils/dips'

export type ReorderRow = HospitalConfigPractitioner & {
    order: number | null
    assignment_id: number
    fullName: string
    status: 'active' | 'inactive'
    department_id: number
}

function arrayMove<T>(arr: readonly T[], from: number, to: number): T[] {
    const next = arr.slice()
    const [moved] = next.splice(from, 1)
    if (moved === undefined) return next
    next.splice(to, 0, moved)
    return next
}

function getSortedPractitionerRows(
    department: Department,
    practitioners: readonly HospitalConfigPractitioner[],
    shouldUsePractitionerOrder: boolean
): ReorderRow[] {
    const today = getToday()
    return practitioners
        .flatMap(practitioner => {
            const assignment = practitioner.departmentAssignments.find(da => da.department_id === department.id)

            if (!assignment) return []
            return [
                {
                    ...practitioner,
                    department_id: department.id,
                    fullName: getPractitionerFullname(practitioner),
                    assignment_id: assignment.id,
                    status: isPractitionerActive(practitioner, today, today) ? 'active' : 'inactive',
                    order: (shouldUsePractitionerOrder ? practitioner.order : assignment.order) ?? null,
                } as const satisfies ReorderRow,
            ]
        })
        .sort((a, b) => {
            const orderA = a.order ?? Number.POSITIVE_INFINITY
            const orderB = b.order ?? Number.POSITIVE_INFINITY
            if (orderA !== orderB) return orderA - orderB
            return a.fullName.localeCompare(b.fullName)
        })
}

function sameOrder(a: readonly string[], b: readonly string[]) {
    return a.length === b.length && a.every((uuid, i) => uuid === b[i])
}

export const useReorderPractitioners = () => {
    const departments = useDepartments()
    const specialities = useSpecialities()

    const allPractitioners = useAllPractitionersForHospitalConfig()
    const shouldUsePractitionerOrder = useFlag('use_specialties_selection')
    const [initialRows] = useState(() => new Map(departments.map(d => [d.uuid, getSortedPractitionerRows(d, allPractitioners, shouldUsePractitionerOrder)])))
    const [rowsByDepartment, setRowsByDepartment] = useState<Map<string, ReorderRow[]>>(initialRows)

    const saveMutation = useMutation({
        mutationFn: async () => {
            const changedRows = Array.from(rowsByDepartment.values()).flatMap(rows =>
                rows.flatMap((r, index) => {
                    const newOrder = index + 1
                    if (r.order === newOrder) return []
                    return [{ row: r, newOrder }]
                })
            )
            if (changedRows.length === 0) return []

            const assignmentUpdates = changedRows.map(({ row, newOrder }) => ({
                id: row.assignment_id,
                body: {
                    department_id: row.department_id,
                    practitioner_id: row.id,
                    order: newOrder,
                },
            }))
            // Practitioner.order tracks the ordering within the practitioner's speciality department only,
            // so mirror it exclusively from the row in that department (see the backfill migration contract).
            const departmentUuidById = new Map(departments.map(d => [d.id, d.uuid]))
            const specialityDepartmentUuidById = new Map(specialities.map(s => [s.id, s.department_uuid]))
            const practitionerUpdates = changedRows.flatMap(({ row, newOrder }) => {
                const specialityDepartmentUuid = specialityDepartmentUuidById.get(row.speciality_id)
                if (specialityDepartmentUuid === undefined || departmentUuidById.get(row.department_id) !== specialityDepartmentUuid) return []
                return [
                    {
                        id: row.id,
                        body: {
                            dips_id: row.dips_id,
                            order: newOrder,
                            role: row.role,
                            short_name: row.short_name,
                            speciality_id: row.speciality_id,
                            speciality_uuid: row.speciality_uuid,
                            uuid: row.uuid,
                        },
                    },
                ]
            })

            await updateBatchDepartmentPractitionerAssignments(assignmentUpdates)
            if (practitionerUpdates.length > 0) await updateBatchPractitioners(practitionerUpdates)
            return assignmentUpdates
        },
        onSuccess: updates => {
            const updatedDepartments = uniqBy(updates, 'body.department_id')
                .map(u => departments.find(d => d.id === u.body.department_id)?.name)
                .filter(Boolean)
            dispatchSuccessToast(
                `Lagret ny rekkefølge for kirurger i ${updatedDepartments.length > 1 ? `${updatedDepartments.length} avdelinger` : (updatedDepartments[0] ?? 'avdelingen')}.`
            )
            void trackUserEvent('HOSPITAL_CONFIGURATION_PRACTITIONER_ORDER_SAVED', {
                departmentCount: updatedDepartments.length,
                practitionerUpdateCount: updates.length,
            })
        },
    })

    const isDirty = !sameOrder(
        Array.from(initialRows.values()).flatMap(r => r.map(r => r.uuid)),
        Array.from(rowsByDepartment.values()).flatMap(r => r.map(r => r.uuid))
    )

    const handleMove = (departmentUuid: string, from: number, to: number) => {
        setRowsByDepartment(prev => {
            const next = new Map(prev)
            const currentRows = next.get(departmentUuid)
            if (!currentRows) return next
            if (from < 0 || to < 0 || from >= currentRows.length || to >= currentRows.length) return next
            const nextRows = arrayMove(currentRows, from, to)
            next.set(departmentUuid, nextRows)
            return next
        })
    }

    const handleDragOver = (departmentUuid: string, { operation: { source, target } }: DragOverEvent) => {
        if (!source || !target || source.id === target.id) return
        const currentRows = rowsByDepartment.get(departmentUuid) ?? []
        const from = currentRows.findIndex(item => item.uuid === String(source.id))
        const to = currentRows.findIndex(item => item.uuid === String(target.id))
        handleMove(departmentUuid, from, to)
    }

    return {
        rows: rowsByDepartment,
        isDirty,
        handleMove,
        handleDragOver,
        mutation: saveMutation,
    }
}
